import { useRef, useState, type DragEvent, type ReactNode } from 'react'
import { Download, FileText, Package, Trash2, Truck, Upload } from 'lucide-react'
import { useDeleteProjectDocument, useUploadProjectDocument } from '@/api/hooks'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ActionPopover } from '@/components/action-popover'
import { LoadingButton } from '@/components/loading-button'
import { useAuth } from '@/hooks/use-auth'
import { cn, parseUTCDate } from '@/lib/utils'
import type { components } from '@/api/schema'
import { FoldToggle } from './fold-toggle'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'
/** Documents listed before "Show all N documents" (USA-1432). */
const DOCUMENT_FOLD = 6

type ProjectDocument = components['schemas']['ProjectDocumentRead']
type ProjectPO = NonNullable<
  NonNullable<components['schemas']['InternalProjectDetailRead']['orders']>[number]['purchase_orders']
>[number]

function formatFileSize(bytes: number | null | undefined) {
  if (!bytes) return null
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function DocumentRow({
  icon,
  title,
  detail,
  onDownload,
  actions,
}: {
  icon: ReactNode
  title: string
  detail: string | null
  onDownload: () => void
  actions?: ReactNode
}) {
  return (
    <li className="group flex items-center gap-2 rounded-md pr-1 hover:bg-muted/60">
      <button
        type="button"
        onClick={onDownload}
        title={`Download ${title}`}
        className="flex min-w-0 flex-1 items-center gap-2.5 rounded-md px-2 py-1.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
      >
        <span className="shrink-0">{icon}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{title}</span>
          {detail && <span className="block truncate text-xs text-muted-foreground">{detail}</span>}
        </span>
        <Download className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
      </button>
      {actions}
    </li>
  )
}

/** Every file on the project in one list: PO PDFs, BOLs, labels, and uploads. */
export function ProjectDocumentsCard({
  projectId,
  documents,
  purchaseOrders,
}: {
  projectId: string
  documents: ProjectDocument[]
  purchaseOrders: ProjectPO[]
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const uploadMutation = useUploadProjectDocument(projectId)
  const deleteMutation = useDeleteProjectDocument(projectId)
  const { getAccessToken } = useAuth()

  const download = async (path: string, filename: string) => {
    const token = await getAccessToken()
    if (!token) return
    const resp = await fetch(`${API_BASE_URL}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!resp.ok) return
    const blob = await resp.blob()
    const href = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = href
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(href)
  }

  const upload = (files: FileList | File[]) => {
    const list = Array.from(files)
    if (list.length > 0) uploadMutation.mutate(list)
  }

  const shipments = purchaseOrders.flatMap((po) =>
    (po.shipments ?? []).map((shipment) => ({ ...shipment, po })),
  )
  const generated = [
    ...purchaseOrders
      .filter((po) => po.pdf_filename)
      .map((po) => ({
        key: `po-${po.uuid}`,
        icon: <FileText className="h-4 w-4 text-blue-600" />,
        title: `PO ${po.po_number}`,
        detail: po.manufacturer?.short_name ?? null,
        path: `/dashboard/purchase-orders/${po.uuid}/pdf`,
        filename: po.pdf_filename ?? `PO-${po.po_number}.pdf`,
      })),
    ...shipments
      .filter((s) => s.bol_pdf_filename)
      .map((s) => ({
        key: `bol-${s.uuid}`,
        icon: <Truck className="h-4 w-4 text-emerald-600" />,
        title: `BOL · ${s.po.po_number}`,
        detail: s.carrier_name ?? null,
        path: `/dashboard/shipments/${s.uuid}/bol`,
        filename: s.bol_pdf_filename ?? 'bol.pdf',
      })),
    ...shipments
      .filter((s) => s.label_pdf_filename)
      .map((s) => ({
        key: `label-${s.uuid}`,
        icon: <Package className="h-4 w-4 text-violet-600" />,
        title: `Shipping label · ${s.po.po_number}`,
        detail: s.carrier_name ?? null,
        path: `/dashboard/shipments/${s.uuid}/label`,
        filename: s.label_pdf_filename ?? 'label.pdf',
      })),
  ]

  return (
    <Card
      className={cn('gap-0 py-0 transition-colors', isDragging && 'border-primary bg-primary/5')}
      onDragOver={(e: DragEvent) => {
        e.preventDefault()
        setIsDragging(true)
      }}
      onDragLeave={(e: DragEvent) => {
        e.preventDefault()
        setIsDragging(false)
      }}
      onDrop={(e: DragEvent) => {
        e.preventDefault()
        setIsDragging(false)
        upload(e.dataTransfer.files)
      }}
    >
      <CardContent className="space-y-2 p-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Documents
          </h3>
          <LoadingButton
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            loading={uploadMutation.isPending}
            loadingText="Uploading..."
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="mr-1 h-3.5 w-3.5" /> Upload
          </LoadingButton>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            aria-label="Upload project documents"
            onChange={(e) => {
              if (e.target.files) upload(e.target.files)
              e.target.value = ''
            }}
          />
        </div>
        {generated.length === 0 && documents.length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground">
            {isDragging ? 'Drop to upload' : 'No documents yet. Drop files here to upload.'}
          </p>
        ) : (
          <>
          <ul className="-mx-2 space-y-0.5">
            {[
            ...generated.map((doc) => (
              <DocumentRow
                key={doc.key}
                icon={doc.icon}
                title={doc.title}
                detail={doc.detail}
                onDownload={() => download(doc.path, doc.filename)}
              />
            )),
            ...documents.map((doc) => (
              <DocumentRow
                key={doc.uuid}
                icon={<FileText className="h-4 w-4 text-muted-foreground" />}
                title={doc.file_name}
                detail={[
                  formatFileSize(doc.file_size),
                  parseUTCDate(doc.created_at).toLocaleDateString(),
                ]
                  .filter(Boolean)
                  .join(' · ')}
                onDownload={() =>
                  download(
                    `/dashboard/projects/${projectId}/documents/${doc.uuid}/download`,
                    doc.file_name,
                  )
                }
                actions={
                  deleteMutation.isPending && deleteMutation.variables === doc.uuid ? (
                    <LoadingButton
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      loading
                      aria-label={`Deleting ${doc.file_name}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </LoadingButton>
                  ) : (
                    <ActionPopover
                      align="end"
                      trigger={
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground transition-opacity hover:text-destructive focus-visible:opacity-100 data-[state=open]:opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                          title="Delete"
                          aria-label={`Delete ${doc.file_name}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      }
                      title={`Delete "${doc.file_name}"?`}
                      confirmLabel="Delete"
                      confirmVariant="destructive"
                      onConfirm={(_note, close) => {
                        close()
                        deleteMutation.mutate(doc.uuid)
                      }}
                    />
                  )
                }
              />
            )),
            ].slice(0, showAll ? undefined : DOCUMENT_FOLD)}
          </ul>
          {generated.length + documents.length > DOCUMENT_FOLD && (
            <FoldToggle
              expanded={showAll}
              total={generated.length + documents.length}
              noun="documents"
              onToggle={() => setShowAll((value) => !value)}
            />
          )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
